//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
//@ts-nocheck
import { Box, Flex, Stack, Text, Textarea, useTheme } from "@chakra-ui/react";
import styled from "@emotion/styled";
import React from "react";
import TextareaAutosize from "react-autosize-textarea";
import { parseStringForTriggers, useTextArea } from "./hook";
import Icon from "../../../components/ui-elements/Icon";

const StyledTextArea: React.FC<any> = styled(TextareaAutosize)`
  width: 100%;
  padding: 16px;
  resize: none;
  overflow: hidden;
  border: ${({ theme, borderwidth }: any) => theme.borderWidths[borderwidth]};
  border-style: solid;
  box-shadow: ${({ isinvalids, theme }: any) =>
    `0 0 0 1px ${isinvalids === "true" ? theme.colors.red["500"] : "none"}`};
  transition: all 0.2s ease-in-out;
  font-size: ${({ fontSize, theme }: any) => theme.fontSizes[fontSize]};
  line-height: ${({ theme, lineheight }: any) => theme.lineHeights[lineheight]};
  border-radius: ${({ theme, borderradius }: any) => theme.radii[borderradius]};
  border-color: ${({ isinvalids, theme }: any) =>
    isinvalids === "true" ? theme.colors.red["500"] : theme.colors.gray["200"]};

  &::placeholder {
    color: ${({ theme }: any) => theme.colors.gray["500"]};
  }

  &:hover {
    border-color: ${({ isinvalids, theme }: any) =>
      isinvalids === "true"
        ? theme.colors.red["500"]
        : theme.colors.gray["300"]};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ isinvalids, theme, color }: any) =>
      `0 0 0 1px ${
        isinvalids === "true"
          ? theme.colors.red["500"]
          : theme.colors[color][500]
      }`};
    border-color: ${({ isinvalids, color, theme }: any) =>
      isinvalids === "true"
        ? theme.colors.red["500"]
        : theme.colors[color]["500"]};
  }

  &:disabled {
    border-color: ${({ theme }: any) => theme.colors.gray["200"]};
    background-color: ${({ theme }: any) => theme.colors.gray["200"]};
    color: ${({ theme }: any) => theme.colors.gray["200"]};
    cursor: not-allowed;
  }
`;

const CharacterCounter = ({ charDiff, children }) => {
  const isNearLimit = charDiff < 16 && charDiff >= 0;
  const hasExceededLimit = charDiff < 0;
  return (
    <Text
      px={2}
      position="absolute"
      right={2}
      bottom={2}
      fontSize="xs"
      bg={isNearLimit ? "gold.200" : hasExceededLimit && "red.400"}
      rounded="md"
      opacity={0.6}
      fontWeight="medium"
    >
      {children}
    </Text>
  );
};

type Props = {
  fontSize: string;
  name: string;
  isInvalid: boolean;
  isReadOnly: boolean;
  isDisabled: boolean;
  innerRef: any;
  characterLimit: any;
  description: string;
  errorMessage: string;
  borderWidth: string;
  lineHeight: string;
  borderRadius: string;
  parseInput: boolean;
  rows: number;
  maxRows: number;
  onResize: () => void;
  color: string;
  defaultValue: string;
  value: any;
  onChange: (value: string) => void;
  className: string;
  streamingText?: string;
  streamingLoading?: boolean;
  [key: string]: any;
};

const ExpandingTextArea = ({
  fontSize = "md",
  name,
  isInvalid,
  isReadOnly,
  isDisabled,
  innerRef,
  characterLimit,
  description,
  errorMessage,
  borderWidth = "2xs",
  lineHeight = "tall",
  borderRadius = "md",
  parseInput = true,
  rows = 11,
  maxRows,
  onResize,
  color = "blue",
  defaultValue,
  value: valueProp = "",
  onChange,
  streamingText = "",
  streamingLoading,
  onBlur,
  className,
  triggers = {},
  ...props
}: Partial<Props>) => {
  const theme = useTheme();
  const { triggerPrompt, value, inputCharacters, isInvalids, handleChange } =
    useTextArea(
      valueProp,
      onChange,
      characterLimit,
      isInvalid,
      parseInput,
      triggers,
      streamingText,
      streamingLoading
    );

  return (
    <Box>
      {description && (
        <Text mt={-1} color="gray.600" fontSize="sm" mb={3}>
          {description}
        </Text>
      )}
      <Box position="relative">
        <Flex align="center" flexWrap="nowrap">
          <StyledTextArea
            disabled={isDisabled}
            autoFocus={false}
            ref={innerRef}
            readOnly={streamingLoading || isReadOnly}
            onChange={handleChange}
            onBlur={onBlur}
            className={`Zeta-TextAreaAutoSize ${className}`}
            borderradius={borderRadius}
            borderwidth={borderWidth}
            lineheight={lineHeight}
            isinvalids={isInvalids ? "true" : "false"}
            {...{
              theme,
              name,
              defaultValue,
              rows,
              maxRows,
              onResize,
              fontSize,
              value: streamingLoading ? streamingText : value,
              color,
            }}
            {...props}
          />
          {characterLimit && (
            <CharacterCounter charDiff={characterLimit - inputCharacters}>
              {inputCharacters} / {characterLimit}
            </CharacterCounter>
          )}
        </Flex>
      </Box>
      {parseInput && !isInvalid && triggerPrompt && (
        <Stack mt={2} color="red.500" isInline align="center" fontSize="sm">
          <Icon name="error" />
          <Text>{triggerPrompt}</Text>
        </Stack>
      )}
    </Box>
  );
};

type TextareaProps = {
  characterLimit?: any;
  characterCount?: any;
  onChange: (value: any) => void;
  isInvalid: boolean;
  parseInput?: boolean;
  value?: string;
  triggers?: any;
  errorMessage?: string;
};

export const EnhancedTextarea = React.forwardRef<any, TextareaProps>(
  (props, ref) => {
    const {
      characterLimit,
      onChange,
      characterCount,
      isInvalid,
      value,
      parseInput = true,
      triggers = {},
      errorMessage,
      ...rest
    } = props;

    const [inputCharacters, setInputCharacters] =
      React.useState(characterCount);
    const triggerPrompt = parseStringForTriggers(value, triggers);

    const isInvalids = parseInput
      ? Boolean(isInvalid || inputCharacters > characterLimit || triggerPrompt)
      : Boolean(isInvalid || inputCharacters > characterLimit);

    React.useEffect(() => {
      setInputCharacters(characterCount);
    }, [characterCount]);

    const handleInputChange = ({ target }) => {
      onChange && onChange(target.value);
      setInputCharacters(target.value.length);
    };
    return (
      <Box>
        <Box position="relative">
          <Flex align="center" flexWrap="nowrap">
            <Textarea
              ref={ref}
              onChange={handleInputChange}
              isInvalid={Boolean(isInvalids || isInvalid)}
              value={value}
              {...rest}
            />
            {characterLimit && (
              <CharacterCounter charDiff={characterLimit - inputCharacters}>
                {inputCharacters} / {characterLimit}
              </CharacterCounter>
            )}
          </Flex>
        </Box>
        {parseInput && !isInvalid && triggerPrompt && (
          <Stack mt={2} color="red.500" isInline align="center" fontSize="sm">
            <Icon name="error" />
            <Text>{triggerPrompt}</Text>
          </Stack>
        )}
      </Box>
    );
  }
);

export const CustomTextarea = React.forwardRef<any, TextareaProps>(
  (
    props: {
      characterLimit: any;
      onChange: (...params) => any;
      initialValue: any;
      isInvalid: boolean;
      parseInput: boolean;
      triggers?: any;
      [key: string]: any;
    },
    ref
  ) => {
    const {
      characterLimit,
      onChange,
      initialValue,
      isInvalid,
      parseInput = true,
      triggers = {},
      ...rest
    } = props;

    const [value, setValue] = React.useState(initialValue);
    const [charactersLeft, setCharactersLeft] = React.useState(characterLimit);
    const triggerPrompt = parseStringForTriggers(value, triggers);

    const isInvalids = parseInput
      ? Boolean(isInvalid || charactersLeft < 0 || triggerPrompt)
      : Boolean(isInvalid || charactersLeft < 0);

    React.useEffect(() => {
      if (value) {
        setCharactersLeft(characterLimit - value.length);
      } else {
        setCharactersLeft(characterLimit - 0);
      }
    }, [value]);

    const handleInputChange = ({ target }) => {
      setValue(target.value);
      setCharactersLeft(characterLimit - target.value.length);
      onChange && onChange(target.value);
    };

    return (
      <Box>
        <Box position="relative">
          <Flex align="center" flexWrap="nowrap">
            <Textarea
              ref={ref}
              onChange={handleInputChange}
              isInvalid={Boolean(isInvalids || isInvalid)}
              value={value}
              {...rest}
            />
            {/* {characterLimit && (
              <CharacterCounter>{charactersLeft}</CharacterCounter>
            )} */}
          </Flex>
        </Box>
        {parseInput && !isInvalid && triggerPrompt && (
          <Stack mt={2} color="red.500" isInline align="center" fontSize="sm">
            <Icon name="error" />
            <Text>{triggerPrompt}</Text>
          </Stack>
        )}
        {characterLimit && charactersLeft < 0 && (
          <Stack mt={2} color="red.500" isInline align="center" fontSize="sm">
            <Icon name="error" />
            <Text>Exceeded number of characters for subject heading</Text>
          </Stack>
        )}
      </Box>
    );
  }
);

export default ExpandingTextArea;
